import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Input, Label, Row, Col, Progress } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

// Alta masiva de vinos desde un Excel (layout). El layout se descarga aqui
// mismo con el boton "Descargar layout".
//
// Reglas: CODIGO, NOMBRE, TIPO, REGION y PROVEEDOR son obligatorios; un
// codigo que ya existe en el sistema (o repetido dentro del archivo) NO se da de
// alta. VARIETAL vacio = sin varietal. EMPRESA vacia = primera empresa activa del
// catalogo. TASA IEPS: cualquier porcentaje de 0 a 100 (vacia = 26.5). CODIGO SAT: 8 digitos; vacio = NA.
// Cada vino nuevo nace con su inventario en CEDIS en 0, igual que el alta normal.
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const env = (k) => process.env[k];
const txt = (v) => String(v === undefined || v === null ? "" : v).trim();
const nu = (v) => Number(String(v ?? "").toString().replace(/[$,\s]/g, "")) || 0;
const norm = (v) => txt(v).toUpperCase().replace(/\s+/g, " ");

// Columnas del layout: [encabezado, obligatorio?, ejemplo]
const COLUMNAS = [
  ["CODIGO", true, "EJEMPLO-BORRAR"],
  ["NOMBRE", true, "CABERNET SAUVIGNON RESERVA"],
  ["DESCRIPCION", false, "TINTO CON CRIANZA EN BARRICA"],
  ["TIPO", true, ""],
  ["REGION", true, ""],
  ["VARIETAL", false, ""],
  ["PROVEEDOR", true, ""],
  ["EMPRESA", false, ""],
  ["ANADA", false, "2021"],
  ["PRESENTACION ML", false, 750],
  ["GRADUACION", false, 13.5],
  ["CERTIFICACION", false, "OU"],
  ["MEVUSHAL", false, "No"],
  ["PESAJ", false, "No"],
  ["TASA IEPS", false, 26.5],
  ["BOTELLAS POR CAJA", false, 12],
  ["COSTO USD", false, 0],
  ["COSTO MX", false, 0],
  ["VENTA", false, 0],
  ["EAN", false, "NA"],
  ["CODIGO SAT", false, "50202203"],
  ["NOTAS DE CATA", false, ""],
  ["MARIDAJE", false, ""],
  ["TEMPERATURA SERVICIO", false, "16-18 C"],
  ["OBSERVACIONES", false, "NA"],
];

function ImportarArticulosModal({ abierto, cerrar, onTerminado, userId }) {
  const [cats, setCats] = useState({});
  const [filas, setFilas] = useState([]);
  const [archivo, setArchivo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [avance, setAvance] = useState(0);

  useEffect(() => {
    if (!abierto) return;
    setFilas([]); setArchivo(""); setAvance(0);
    setCats({});
    const pide = (url) => axios.get(url, { headers: headers() }).then((r) => r.data || []).catch(() => []);
    // Los codigos existentes (para duplicados) van aparte y por la ruta ligera:
    // la lista completa de articulos pesa ~13 MB y tardaba en abrir el layout.
    pide(`${env("REACT_APP_URL_ARTICULOS")}Select`).then((articulos) =>
      setCats((c) => ({ ...c, articulos }))
    );
    
  }, [abierto]);

  // Busca en un catalogo por nombre (ignora mayusculas y espacios de mas)
  const busca = (lista, valor, campo = "name") => {
    const t = norm(valor);
    if (!t) return null;
    return (lista || []).find((x) => norm(x[campo]) === t) || null;
  };
  const activos = (lista) => (lista || []).filter((x) => x.is_active !== "No");

  function descargaLayout() {
    const wb = XLSX.utils.book_new();
    // Hoja 1: encabezados + un renglon de ejemplo
    const hoja = XLSX.utils.aoa_to_sheet([
      COLUMNAS.map(([c, ob]) => (ob ? `${c} *` : c)),
      COLUMNAS.map(([, , ej]) => ej),
    ]);
    hoja["!cols"] = COLUMNAS.map(([c]) => ({ wch: Math.max(14, c.length + 4) }));
    XLSX.utils.book_append_sheet(wb, hoja, "Vinos");
    // Hoja 2: como llenarlo + catalogos vivos
    const nombres = (lista, campo = "name") =>
      activos(lista).map((x) => txt(x[campo])).filter(Boolean).sort().join(" | ");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ["COMO LLENAR ESTE LAYOUT"],
      [],
      ["1", "Captura un vino por renglon, empezando en el renglon 3. El renglon 2 es un ejemplo: se puede borrar, y aunque se deje no se da de alta."],
      ["2", "Las columnas con * son obligatorias: CODIGO, NOMBRE, TIPO, REGION y PROVEEDOR."],
      ["3", "El CODIGO no se puede repetir. Si ya existe en el sistema o se repite en el archivo, ese renglon no se da de alta."],
      ["4", "TIPO, REGION, VARIETAL, PROVEEDOR y EMPRESA se buscan por nombre: copialos tal cual de la lista de abajo."],
      ["5", "VARIETAL vacio = sin varietal. EMPRESA vacia = la primera empresa activa del catalogo."],
      ["6", "ANADA: el anio de cosecha o NV. MEVUSHAL y PESAJ: Si o No. TASA IEPS: porcentaje de 0 a 100; vacia = 26.5."],
      ["7", "CODIGO SAT: clave de producto del SAT de 8 digitos."],
      ["8", "Los importes, BOTELLAS POR CAJA, PRESENTACION ML y GRADUACION van en numero, sin signos; vacios quedan en 0 (presentacion = 750)."],
      ["9", "No cambies los nombres de las columnas."],
      [],
      ["CATALOGOS DISPONIBLES (copia el nombre tal cual)"],
      ["Tipos de vino", nombres(cats.lineas)],
      ["Regiones", nombres(cats.familias)],
      ["Varietales", nombres(cats.colecciones)],
      ["Proveedores", nombres(cats.proveedores, "nombre_comercial")],
      ["Empresas", nombres(cats.empresas)],
    ]), "Instrucciones");
    XLSX.writeFile(wb, "LayoutAltaVinos.xlsx");
  }

  function leeArchivo(file) {
    if (!file) return;
    setArchivo(file.name); setAvance(0);
    const lector = new FileReader();
    lector.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const hoja = wb.Sheets[wb.SheetNames[0]];
        const crudas = XLSX.utils.sheet_to_json(hoja, { defval: "" });
        setFilas(revisa(crudas));
      } catch (err) {
        Swal.fire({ icon: "error", title: "No se pudo leer el archivo", text: "Usa el layout que se descarga de este mismo boton." });
      }
    };
    lector.readAsArrayBuffer(file);
  }

  // Valida cada renglon y deja listo el cuerpo que se va a mandar
  function revisa(crudas) {
    const dame = (f, col) => {
      const k = Object.keys(f).find((x) => norm(x).replace(/\*/g, "").trim() === col);
      return k ? txt(f[k]) : "";
    };
    const existentes = new Set((cats.articulos || []).map((a) => norm(a.codigo)));
    const vistos = new Set();
    return crudas.map((f, i) => {
      const codigo = dame(f, "CODIGO");
      const nombre = dame(f, "NOMBRE");
      const fila = { renglon: i + 2, codigo, nombre };
      if (!codigo && !nombre) return { ...fila, estado: "vacio" };
      // El renglon de ejemplo del layout se ignora aunque no lo borren
      if (norm(codigo).startsWith("EJEMPLO")) return { ...fila, estado: "vacio" };

      const faltan = ["CODIGO", "NOMBRE", "TIPO", "REGION", "PROVEEDOR"].filter((c) => !dame(f, c));
      if (faltan.length) return { ...fila, estado: "error", detalle: `Falta ${faltan.join(", ")}` };
      if (existentes.has(norm(codigo))) return { ...fila, estado: "duplicado", detalle: "Ya existe en el sistema" };
      if (vistos.has(norm(codigo))) return { ...fila, estado: "duplicado", detalle: "Repetido en el archivo" };

      // Catalogos obligatorios: si no existen, el renglon no pasa
      const noExisten = [];
      const obligatorio = (col, lista, campo = "name") => {
        const x = busca(lista, dame(f, col), campo);
        if (!x) noExisten.push(`${col} "${dame(f, col)}"`);
        return x && x._id;
      };
      const lineas = obligatorio("TIPO", cats.lineas);
      const familias = obligatorio("REGION", cats.familias);
      const proveedores = obligatorio("PROVEEDOR", cats.proveedores, "nombre_comercial");
      const empresaTxt = dame(f, "EMPRESA");
      const empresa = empresaTxt ? busca(cats.empresas, empresaTxt) : activos(cats.empresas)[0];
      if (!empresa) noExisten.push(empresaTxt ? `EMPRESA "${empresaTxt}"` : "EMPRESA: no hay empresas activas en el catalogo");
      if (noExisten.length) return { ...fila, estado: "error", detalle: `No existe ${noExisten.join(", ")}` };

      const avisos = [];
      const varietalTxt = dame(f, "VARIETAL");
      const varietal = busca(cats.colecciones, varietalTxt);
      if (varietalTxt && !varietal) avisos.push("VARIETAL no existe: queda sin varietal");

      const tasaTxt = dame(f, "TASA IEPS");
      const tasaIEPS = tasaTxt ? nu(tasaTxt) : 26.5;
      if (!(tasaIEPS >= 0 && tasaIEPS <= 100)) {
        return { ...fila, estado: "error", detalle: `TASA IEPS "${tasaTxt}" invalida: usa un porcentaje de 0 a 100` };
      }
      const siNo = (col) => (norm(dame(f, col)).startsWith("S") ? "Si" : "No");

      // Codigo SAT: 8 digitos; vacio = NA con aviso
      const satTxt = dame(f, "CODIGO SAT").replace(/\s/g, "");
      if (satTxt && !/^\d{8}$/.test(satTxt)) {
        return { ...fila, estado: "error", detalle: `CODIGO SAT "${satTxt}" invalido: deben ser 8 digitos` };
      }
      if (!satTxt) avisos.push("sin CODIGO SAT");

      vistos.add(norm(codigo));
      const oNAtxt = (col) => dame(f, col) || "NA";
      const anada = dame(f, "ANADA") || "NV";
      const cuerpo = {
        codigo, nombre,
        descripcion: oNAtxt("DESCRIPCION"),
        piezasPorCaja: nu(dame(f, "BOTELLAS POR CAJA")),
        costoUSD: nu(dame(f, "COSTO USD")),
        costoMX: nu(dame(f, "COSTO MX")),
        venta: nu(dame(f, "VENTA")),
        observaciones: oNAtxt("OBSERVACIONES"),
        ano: anada,
        anada,
        presentacionMl: nu(dame(f, "PRESENTACION ML")) || 750,
        graduacion: nu(dame(f, "GRADUACION")),
        certificacion: dame(f, "CERTIFICACION"),
        mevushal: siNo("MEVUSHAL"),
        kosherPesaj: siNo("PESAJ"),
        notasCata: dame(f, "NOTAS DE CATA"),
        maridaje: dame(f, "MARIDAJE"),
        tempServicio: dame(f, "TEMPERATURA SERVICIO"),
        tasaIEPS,
        EAN: oNAtxt("EAN"),
        codigoSAT: satTxt || "NA",
        codigoUnidad: "NA",
        lineas, familias, proveedores,
        ...(varietal ? { colecciones: varietal._id } : {}),
        empresas: empresa._id,
        pesoCaja: 0, tamanoCaja: "0", vendedores: "Si", visibleInventario: "Si",
      };
      return { ...fila, estado: "nuevo", cuerpo, detalle: avisos.join(" · ") };
    }).filter((f) => f.estado !== "vacio");
  }

  const nuevos = filas.filter((f) => f.estado === "nuevo");
  const duplicados = filas.filter((f) => f.estado === "duplicado");
  const errores = filas.filter((f) => f.estado === "error");

  async function guardar() {
    if (!nuevos.length) return;
    const ok = await Swal.fire({
      icon: "question", title: `Se daran de alta ${nuevos.length} vino(s)`,
      html: duplicados.length || errores.length
        ? `<b style="color:#b45309">${duplicados.length} duplicado(s) y ${errores.length} con error se omiten.</b>`
        : "",
      showCancelButton: true, confirmButtonText: "Si, dar de alta", cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setGuardando(true);
    const fallaron = [];
    for (let i = 0; i < nuevos.length; i++) {
      try {
        const r = await axios.post(env("REACT_APP_URL_ARTICULOS"), nuevos[i].cuerpo, { headers: headers() });
        // Su renglon de inventario en CEDIS, igual que el alta de un vino
        await axios.post(
          env("REACT_APP_URL_INVENTARIOS"),
          { articulos: r.data._id, areas: env("REACT_APP_AREAS_CEDIS") },
          { headers: headers() }
        );
      } catch (error) {
        fallaron.push(`${nuevos[i].codigo}: ${error.response?.data?.message || error.message}`);
      }
      setAvance(Math.round(((i + 1) / nuevos.length) * 100));
    }
    try {
      await axios.post(
        env("REACT_APP_URL_LOGS"),
        { tipo: "Alta masiva vinos", detalle: `${nuevos.length - fallaron.length} vinos desde layout`, user: userId },
        { headers: headers() }
      );
    } catch (e) { /* el log no detiene el alta */ }
    setGuardando(false);
    await Swal.fire({
      icon: fallaron.length ? "warning" : "success",
      title: `${nuevos.length - fallaron.length} vino(s) dados de alta`,
      html: fallaron.length ? `No se pudieron dar de alta:<br><small>${fallaron.slice(0, 10).join("<br>")}</small>` : "",
    });
    cerrar();
    if (onTerminado) onTerminado();
  }

  const color = { nuevo: "#15803d", duplicado: "#b45309", error: "#dc2626" };
  return (
    <Modal size="lg" isOpen={abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>Alta de vinos desde layout</ModalHeader>
      <ModalBody>
        <Row>
          <Col md={5}>
            <Label><b>1. Layout</b></Label>
            <div>
              <Button size="sm" color="info" onClick={descargaLayout} disabled={!cats.lineas}>
                <i className="far fa-file-excel" /> Descargar layout
              </Button>
            </div>
            <small style={{ color: "#64748b" }}>
              {cats.lineas ? "Trae las instrucciones y los catálogos para llenarlo." : "Cargando catálogos..."}
            </small>
          </Col>
          <Col md={7}>
            <Label><b>2. Sube el layout lleno</b></Label>
            <Input type="file" accept=".xlsx,.xls" disabled={!cats.articulos || !cats.lineas} onChange={(e) => leeArchivo(e.target.files[0])} />
            {!cats.articulos || !cats.lineas ? (
              <small style={{ color: "#b45309" }}>Cargando códigos existentes...</small>
            ) : archivo ? (
              <small style={{ color: "#64748b" }}>{archivo}</small>
            ) : null}
          </Col>
        </Row>
        <br />
        {filas.length ? (
          <>
            <div style={{ display: "flex", gap: 14, marginBottom: 6, fontSize: 13 }}>
              <span style={{ color: color.nuevo, fontWeight: 700 }}>{nuevos.length} por dar de alta</span>
              <span style={{ color: color.duplicado, fontWeight: 700 }}>{duplicados.length} duplicados</span>
              <span style={{ color: color.error, fontWeight: 700 }}>{errores.length} con error</span>
            </div>
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              <Table size="sm" bordered>
                <thead>
                  <tr><th>Renglón</th><th>Código</th><th>Nombre</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {filas.map((f) => (
                    <tr key={f.renglon}>
                      <td>{f.renglon}</td>
                      <td>{f.codigo}</td>
                      <td>{f.nombre}</td>
                      <td style={{ color: color[f.estado], fontWeight: 600 }}>
                        {f.estado === "nuevo" ? (f.detalle ? `Se dará de alta · ${f.detalle}` : "Se dará de alta") : f.detalle}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        ) : (
          <p style={{ color: "#9ca3af" }}>Sube el layout para ver qué se va a dar de alta.</p>
        )}
        {guardando ? <Progress animated value={avance}>{avance}%</Progress> : null}
      </ModalBody>
      <ModalFooter>
        <Button color="success" onClick={guardar} disabled={guardando || !nuevos.length}>
          {guardando ? "Dando de alta..." : `Dar de alta ${nuevos.length} vino(s)`}
        </Button>
        <Button color="secondary" onClick={cerrar} disabled={guardando}>Cerrar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default ImportarArticulosModal;
